/**
 * Inquiries API — outreach_inquiries table
 *
 * NOTE: outreach_inquiries has no deleted_at column.
 * Lifecycle: new → in_review → contacted → converted → closed → archived
 * Only archived status fully removes records from all views.
 * Only admins may archive. Staff may close.
 */
import { supabase } from '@/lib/supabase'

export type InquiryStatus =
  | 'new' | 'in_review' | 'contacted' | 'awaiting_response' | 'converted'
  | 'closed' | 'duplicate' | 'archived'

export interface OutreachInquiry {
  id:          string
  first_name:  string
  last_name:   string
  email:       string | null
  phone:       string | null
  notes:       string | null
  status:      InquiryStatus | string
  assigned_to: string | null
  priority:    string | null
  created_at:  string
  updated_at:  string
  duplicate_of: string | null
  duplicate_reason: string | null
  duplicate_marked_by: string | null
  duplicate_marked_at: string | null
}

export interface InquiryFilters {
  status?:     InquiryStatus | ''
  assignedTo?: string
  limit?:      number
}

export interface OutreachInquiryWithAssignee extends OutreachInquiry {
  assignee?: { id: string; display_name: string | null } | null
}

export const getInquiries = async (
  filters: InquiryFilters = {}
): Promise<OutreachInquiryWithAssignee[]> => {
  let q = supabase
    .from('outreach_inquiries')
    .select('*, assignee:profiles!outreach_inquiries_assigned_to_fkey(id, display_name)')
    .order('created_at', { ascending: false })

  if (filters.status) {
    q = q.eq('status', filters.status)
  } else {
    // Default active view: exclude closed and archived
    q = q.neq('status', 'closed').neq('status', 'archived')
  }
  if (filters.assignedTo) q = q.eq('assigned_to', filters.assignedTo)
  if (filters.limit)      q = q.limit(filters.limit)

  const { data, error } = await q
  if (error) throw error
  return (data ?? []) as OutreachInquiryWithAssignee[]
}

export const getInquiry = async (id: string): Promise<OutreachInquiry | null> => {
  const { data, error } = await supabase
    .from('outreach_inquiries')
    .select('*')
    .eq('id', id)
    .single()
  if (error) return null
  return data as OutreachInquiry
}

export const updateInquiry = async (
  id: string,
  payload: Partial<Pick<OutreachInquiry, 'status' | 'assigned_to' | 'priority'>>
): Promise<void> => {
  const { data, error, status, statusText } = await supabase
    .from('outreach_inquiries')
    .update({ ...payload, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select('id')   // diagnostic: tells us whether SELECT policy allows the read-back

  // Full response logged so we can see exactly what Supabase returns:
  //   data non-empty  → update succeeded and SELECT policy allows read-back
  //   data empty, no error → update may have succeeded but SELECT is RLS-blocked
  //   error present   → actual failure (wrong type, FK violation, etc.)
  console.log('[updateInquiry]', { id, payload, data, error, status, statusText })

  if (error) throw error

  if (!data || data.length === 0) {
    // Two possibilities:
    // A) UPDATE succeeded but the SELECT read-back is blocked by a missing SELECT policy
    // B) The row doesn't exist or the UPDATE USING clause excluded this row
    // Do NOT throw here — if the update went through we don't want a false failure.
    // The console log above will show which case this is.
    console.warn('[updateInquiry] zero rows returned — update may have succeeded ' +
      'but SELECT policy may be blocking read-back. Verify assigned_to in Supabase.')
  }
}

export const getStaffForAssignment = async () => {
  const { data, error } = await supabase
    .from('profiles')
    .select('id, display_name, first_name, last_name, role')
    .eq('is_active', true)
    .is('deleted_at' as never, null)
    .order('display_name')
  if (error) throw error
  return (data ?? []) as Array<{
    id: string
    display_name: string | null
    first_name:   string | null
    last_name:    string | null
    role:         string
  }>
}

export const getInquiryStats = async () => {
  // Active = not closed or archived
  const { data, error } = await supabase
    .from('outreach_inquiries')
    .select('status')
    .neq('status', 'closed')
    .neq('status', 'archived')

  if (error) throw error
  const rows = (data ?? []) as Array<{ status: string }>
  return {
    total:     rows.length,
    new:       rows.filter(r => r.status === 'new').length,
    inReview:  rows.filter(r => r.status === 'in_review').length,
    converted: rows.filter(r => r.status === 'converted').length,
  }
}

export interface ConversionResult {
  inquiry_id:   string
  household_id: string
  case_id:      string
  assigned_to:  string | null
  county_id:    string | null
  status:       string
}

/** Transactional Inquiry → Case conversion via Postgres RPC.
 *  All five DB operations (household, case, timeline note, inquiry update)
 *  run in a single atomic transaction — rollback on any failure.
 *
 *  Responsibility split:
 *    React  → extracts county name (extractCounty), detects type (detectType)
 *    SQL    → county name → UUID join, transaction, timeline INSERT
 */
export const convertInquiryToCase = async (
  inquiryId: string,
  params: {
    assignedTo?:    string | null   // inquiry.assigned_to
    countyName?:    string | null   // from extractCounty(inquiry.notes)
    sourceType?:    string          // from detectType(inquiry.notes)
    convertingUser?: string | null  // auth.user.id for timeline authorship
  } = {}
): Promise<ConversionResult> => {
  const { data, error } = await supabase.rpc('wee_convert_inquiry_to_case', {
    p_inquiry_id:      inquiryId,
    p_assigned_to:     params.assignedTo     ?? null,
    p_county_name:     params.countyName     ?? null,
    p_source_type:     params.sourceType     ?? 'Support Request',
    p_converting_user: params.convertingUser ?? null,
  })

  // Always log the full RPC response so the exact failing statement is visible
  // in the browser console. Remove once conversion is stable in production.
  console.log('[CASE CONVERSION] rpc response:', {
    data,
    error_code:    error?.code,
    error_message: error?.message,
    error_details: error?.details,
    error_hint:    error?.hint,
  })

  if (error) {
    // Re-throw as a plain Error so the catch block in handleConvert gets a real message
    const detail = [error.message, error.details, error.hint].filter(Boolean).join(' | ')
    throw new Error(detail || 'RPC wee_convert_inquiry_to_case failed')
  }
  return data as ConversionResult
}

/** Strict lifecycle update after logging contact. Unlike the generic update helper,
 * this verifies a row was actually updated so the UI cannot report a successful move
 * when RLS or another database rule prevented the status change. */
export const updateInquiryAfterContact = async (
  id: string,
  status: Extract<InquiryStatus, 'contacted' | 'awaiting_response'>,
): Promise<void> => {
  const { data, error } = await supabase
    .from('outreach_inquiries')
    .update({ status, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select('id, status')
    .maybeSingle()
  if (error) throw error
  if (!data) throw new Error('The contact attempt was saved, but the inquiry status did not update. Your staff permissions or the inquiry status may need checking.')
}

/** Close an inquiry — removes it from the active queue.
 *  Available to all staff. Inquiry remains recoverable via archived filter. */
export const closeInquiry = async (id: string): Promise<void> => {
  const { error } = await supabase
    .from('outreach_inquiries')
    .update({ status: 'closed', updated_at: new Date().toISOString() })
    .eq('id', id)
  if (error) throw error
}

/** Archive an inquiry — admin-only. Removes from all default views.
 *  Archived inquiries can be restored via the status filter. */
export const deleteInquiry = async (id: string): Promise<void> => {
  const { error } = await supabase
    .from('outreach_inquiries')
    .delete()
    .eq('id', id)
  if (error) throw error
}

export const archiveInquiry = async (id: string): Promise<void> => {
  const { error } = await supabase
    .from('outreach_inquiries')
    .update({ status: 'archived', updated_at: new Date().toISOString() })
    .eq('id', id)
  if (error) throw error
}


export const getCaseForInquiry = async (inquiryId: string): Promise<{
  id: string
  case_number: string
  household_id: string
  status: string
  returned_to_inquiry_at: string | null
} | null> => {
  const { data, error } = await supabase
    .from('cases')
    .select('id, case_number, household_id, status, returned_to_inquiry_at')
    .eq('source_inquiry_id', inquiryId)
    .is('deleted_at', null)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle()
  if (error) throw error
  return data as { id: string; case_number: string; household_id: string; status: string; returned_to_inquiry_at: string | null } | null
}

export const markInquiryDuplicate = async (
  duplicateInquiryId: string,
  primaryInquiryId: string,
  reason: string,
): Promise<OutreachInquiry> => {
  const { data, error } = await supabase.rpc('wee_mark_inquiry_duplicate', {
    p_duplicate_inquiry_id: duplicateInquiryId,
    p_primary_inquiry_id: primaryInquiryId,
    p_reason: reason,
  })
  if (error) throw new Error([error.message, error.details, error.hint].filter(Boolean).join(' | '))
  return data as OutreachInquiry
}


/**
 * Remove an incorrect duplicate relationship without deleting either inquiry.
 * If the relationship is reciprocal, clear the matching reverse link too.
 * This is intended for cases where a second submission requests a different
 * service and must remain independently trackable.
 */
export const unlinkInquiryDuplicate = async (inquiryId: string): Promise<void> => {
  const { data: current, error: readError } = await supabase
    .from('outreach_inquiries')
    .select('id, duplicate_of')
    .eq('id', inquiryId)
    .single()
  if (readError) throw readError
  if (!current?.duplicate_of) return

  const linkedId = current.duplicate_of
  const clearedAt = new Date().toISOString()
  const clearFields = {
    duplicate_of: null,
    duplicate_reason: null,
    duplicate_marked_by: null,
    duplicate_marked_at: null,
    updated_at: clearedAt,
  }

  const { error: currentError } = await supabase
    .from('outreach_inquiries')
    .update(clearFields)
    .eq('id', inquiryId)
    .eq('duplicate_of', linkedId)
  if (currentError) throw currentError

  // Clear the reverse pointer only when it points directly back to this row.
  // Never change an unrelated duplicate relationship on the linked inquiry.
  const { error: reverseError } = await supabase
    .from('outreach_inquiries')
    .update(clearFields)
    .eq('id', linkedId)
    .eq('duplicate_of', inquiryId)
  if (reverseError) throw reverseError
}


export type InquiryContactOutcome =
  | 'spoke_with_client'
  | 'no_answer'
  | 'voicemail_left'
  | 'voicemail_full'
  | 'number_unavailable'
  | 'follow_up_needed'

export interface InquiryContactAttempt {
  id: string
  inquiry_id: string | null
  attempted_by: string
  outcome: InquiryContactOutcome
  note: string | null
  notification_channel: 'email' | 'none'
  notification_status: 'pending' | 'sent' | 'failed' | 'not_configured' | 'not_sent' | 'no_email'
  notification_error: string | null
  follow_up_due_at: string
  created_at: string
}

export const getInquiryContactAttempts = async (inquiryId: string): Promise<InquiryContactAttempt[]> => {
  const { data, error } = await supabase
    .from('inquiry_contact_attempts')
    .select('*')
    .eq('inquiry_id', inquiryId)
    .order('created_at', { ascending: false })
    .limit(20)
  if (error) throw error
  return (data ?? []) as InquiryContactAttempt[]
}

/** Records an outreach attempt first, then requests a server-side client email.
 * A missing/unconfigured email provider never erases the contact-attempt record. */
export const recordInquiryContactAttempt = async (input: {
  inquiryId: string
  outcome: InquiryContactOutcome
  note?: string
}): Promise<{ attempt: InquiryContactAttempt; notification: 'sent' | 'not_sent' | 'unavailable'; internalNotification: 'sent' | 'failed' | 'unavailable' }> => {
  const { data: authData, error: authError } = await supabase.auth.getUser()
  if (authError || !authData.user) throw new Error('Your session expired. Please sign in again.')

  const { data, error } = await supabase
    .from('inquiry_contact_attempts')
    .insert({
      inquiry_id: input.inquiryId,
      attempted_by: authData.user.id,
      outcome: input.outcome,
      note: input.note?.trim() || null,
      notification_channel: 'email',
      notification_status: 'pending',
    })
    .select('*')
    .single()
  if (error) throw error

  const { data: notifyData, error: notifyError } = await supabase.functions.invoke('client-contact-notification', {
    body: { contact_attempt_id: data.id },
  })
  if (notifyError) {
    console.warn('[WEE contact workflow] Attempt saved, but notification function is not available:', notifyError.message)
    return { attempt: data as InquiryContactAttempt, notification: 'unavailable', internalNotification: 'unavailable' }
  }
  return {
    attempt: data as InquiryContactAttempt,
    notification: notifyData?.sent === true ? 'sent' : 'not_sent',
    internalNotification: notifyData?.internal_notification_sent === true ? 'sent' : 'failed',
  }
}
